import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Button, Segmented, Select, Tooltip } from 'antd';
import {
  ArrowRight,
  Banknote,
  Boxes,
  CalendarClock,
  Download,
  PackageSearch,
  PackageX,
  RefreshCw,
  ShoppingCart,
  Store,
  TrendingUp,
  TriangleAlert,
  Wallet,
} from 'lucide-react';

import {
  BranchSelect,
  Card,
  ChartSkeleton,
  DataTable,
  DateRangeFilter,
  EmptyState,
  ErrorState,
  KpiCard,
  PageHeader,
  Skeleton,
  StockBadge,
  LOW_STOCK_THRESHOLD,
} from '../../components';
import { DonutChart, HBarChart, StackedBarChart, TrendChart } from '../../components/Charts';
import { useI18n } from '../../i18n';
import { useTheme } from '../../theme';
import { ROUTE_URLS } from '../../routes';
import {
  createLink,
  exportCsv,
  formatDate,
  formatMoney,
  formatNumber,
  formatPercent,
  getPresetRange,
} from '../../utils';
import { useDashboardData } from './useDashboardData';

const LOW_STOCK_PREVIEW = 6;

export const Dashboard = () => {
  const { isAdmin } = useSelector(state => state.authReducer);
  const { t } = useI18n();
  const { palette } = useTheme();

  const [period, setPeriod] = useState(() => ({ preset: 'last7', range: getPresetRange('last7') }));
  const [branchId, setBranchId] = useState(0);
  const [productIds, setProductIds] = useState([]);
  const [metric, setMetric] = useState('revenue');

  const data = useDashboardData({ range: period.range, branchId, productIds, isAdmin });
  const { kpis, status } = data;
  const productFiltered = productIds.length > 0;

  const money = formatMoney;
  const kpiLoading = status.revenue.loading || status.reports.loading;
  const hasAnySales = kpis.revenue > 0 || kpis.units > 0;

  const metricOptions = [
    { value: 'revenue', label: t('charts.revenue') },
    { value: 'units', label: t('charts.units') },
    ...(isAdmin ? [{ value: 'profit', label: t('charts.profit') }] : []),
  ];
  const metricFormatter = metric === 'units' ? v => `${formatNumber(v)} ${t('common.pcs')}` : money;

  const productOptions = useMemo(
    () => data.products.map(p => ({ value: p.id, label: p.name })),
    [data.products],
  );

  const shareData = useMemo(
    () =>
      data.productShare.map(item =>
        item.name === '__others__' ? { ...item, name: t('charts.others') } : item,
      ),
    [data.productShare, t],
  );

  const resetFilters = () => {
    setPeriod({ preset: 'last7', range: getPresetRange('last7') });
    setBranchId(0);
    setProductIds([]);
  };
  const filtersChanged = period.preset !== 'last7' || branchId !== 0 || productFiltered;

  const dailyColumns = [
    {
      title: t('tables.date'),
      dataIndex: 'date',
      render: date => <span className='wm-strong'>{formatDate(date, 'D MMMM YYYY, dd')}</span>,
      sorter: (a, b) => a.date.localeCompare(b.date),
    },
    {
      title: t('charts.revenue'),
      dataIndex: 'revenue',
      align: 'right',
      render: v => money(v),
      sorter: (a, b) => a.revenue - b.revenue,
    },
    {
      title: t('charts.units'),
      dataIndex: 'units',
      align: 'right',
      render: v => formatNumber(v),
      sorter: (a, b) => a.units - b.units,
    },
    ...(isAdmin
      ? [
          {
            title: t('charts.profit'),
            dataIndex: 'profit',
            align: 'right',
            render: v => money(v),
            sorter: (a, b) => a.profit - b.profit,
          },
        ]
      : []),
  ];

  const exportDaily = () => {
    exportCsv(
      `wm-${period.range[0]}_${period.range[1]}`,
      [
        t('tables.date'),
        t('charts.revenue'),
        t('charts.units'),
        ...(isAdmin ? [t('charts.profit')] : []),
      ],
      data.dailyRows.map(r => [r.date, r.revenue, r.units, ...(isAdmin ? [r.profit] : [])]),
    );
  };

  const kpiCards = [
    <KpiCard
      key='revenue'
      label={t('dashboard.kpi.revenue')}
      icon={Wallet}
      value={kpis.revenue}
      format={formatNumber}
      unit={t('common.currency')}
      delta={status.revenuePrev.loading ? undefined : kpis.revenueDelta}
      deltaLabel={t('dashboard.vsPrevious')}
      spark={data.sparks.revenue}
      sparkColor={palette.chart[0]}
      loading={status.revenue.loading}
    />,
    <KpiCard
      key='today'
      label={t('dashboard.kpi.today')}
      icon={CalendarClock}
      tone='neutral'
      value={kpis.today}
      format={formatNumber}
      unit={t('common.currency')}
      delta={kpis.todayDelta}
      deltaLabel={t('dashboard.vsYesterday')}
      loading={status.today.loading}
    />,
    <KpiCard
      key='units'
      label={t('dashboard.kpi.units')}
      icon={ShoppingCart}
      tone='neutral'
      value={kpis.units}
      format={formatNumber}
      unit={t('common.pcs')}
      delta={status.reportsPrev.loading ? undefined : kpis.unitsDelta}
      deltaLabel={t('dashboard.vsPrevious')}
      spark={data.sparks.units}
      sparkColor={palette.chart[1]}
      loading={status.reports.loading}
    />,
    isAdmin ? (
      <KpiCard
        key='profit'
        label={t('dashboard.kpi.profit')}
        icon={TrendingUp}
        tone='success'
        value={kpis.profit}
        format={formatNumber}
        unit={t('common.currency')}
        delta={status.reportsPrev.loading ? undefined : kpis.profitDelta}
        deltaLabel={t('dashboard.vsPrevious')}
        hint={kpis.margin != null ? t('dashboard.margin', { value: formatPercent(kpis.margin) }) : undefined}
        spark={data.sparks.profit}
        sparkColor={palette.chart[2]}
        loading={status.reports.loading}
      />
    ) : (
      <KpiCard
        key='branches'
        label={t('dashboard.kpi.branches')}
        icon={Store}
        tone='neutral'
        value={kpis.activeBranches}
        format={formatNumber}
        hint={t('dashboard.branchesHint', { count: kpis.totalBranches })}
        loading={kpiLoading || status.breakdown.loading}
      />
    ),
  ];

  const adminCards = isAdmin
    ? [
        <KpiCard
          key='avg'
          label={t('dashboard.kpi.avgDaily')}
          icon={Banknote}
          tone='neutral'
          value={kpis.avgDaily}
          format={formatNumber}
          unit={t('common.currency')}
          hint={t('dashboard.daysWithSales', { count: kpis.salesDays })}
          loading={status.revenue.loading}
        />,
        <KpiCard
          key='stock'
          label={t('dashboard.kpi.stock')}
          icon={Boxes}
          tone='neutral'
          value={kpis.hasStock ? kpis.stockUnits : null}
          format={formatNumber}
          unit={t('common.pcs')}
          hint={t('dashboard.skuCount', { count: kpis.skuCount })}
          loading={status.products.loading}
        />,
        <KpiCard
          key='low'
          label={t('dashboard.kpi.lowStock')}
          icon={TriangleAlert}
          tone={kpis.lowStock.length ? 'warning' : 'success'}
          value={kpis.hasStock ? kpis.lowStock.length : null}
          format={formatNumber}
          hint={t('dashboard.lowStockHint', { count: LOW_STOCK_THRESHOLD })}
          loading={status.products.loading}
        />,
        <KpiCard
          key='branches'
          label={t('dashboard.kpi.branches')}
          icon={Store}
          tone='neutral'
          value={kpis.activeBranches}
          format={formatNumber}
          hint={t('dashboard.branchesHint', { count: kpis.totalBranches })}
          loading={kpiLoading || status.breakdown.loading}
        />,
      ]
    : [];

  const trendSeries = data.series[metric];

  return (
    <>
      <PageHeader
        title={t('dashboard.title')}
        subtitle={t('dashboard.subtitle')}
        actions={
          <>
            <Tooltip title={t('common.refresh')}>
              <Button
                aria-label={t('common.refresh')}
                icon={<RefreshCw size={15} className={status.fetching ? 'wm-spin' : undefined} />}
                onClick={data.refetchAll}
              />
            </Tooltip>
            <Link to={ROUTE_URLS.REPORTS}>
              <Button icon={<ArrowRight size={15} />}>{t('dashboard.openReports')}</Button>
            </Link>
          </>
        }
      />

      <div className='wm-filter-bar'>
        <DateRangeFilter value={period} onChange={setPeriod} />
        <BranchSelect
          branches={data.branches}
          value={branchId}
          onChange={setBranchId}
          loading={status.branches.loading}
        />
        <Select
          mode='multiple'
          allowClear
          showSearch
          optionFilterProp='label'
          maxTagCount='responsive'
          placeholder={t('filters.allProducts')}
          value={productIds}
          onChange={setProductIds}
          options={productOptions}
          suffixIcon={<PackageSearch size={15} strokeWidth={1.75} />}
          style={{ minWidth: 220, maxWidth: 360, flex: '1 1 220px' }}
        />
        {filtersChanged && (
          <Button type='text' onClick={resetFilters}>
            {t('filters.reset')}
          </Button>
        )}
      </div>

      <div className='wm-grid wm-grid--kpi'>{kpiCards}</div>
      {adminCards.length > 0 && <div className='wm-grid wm-grid--kpi'>{adminCards}</div>}

      <div className='wm-grid wm-grid--main-side'>
        <Card
          title={t('charts.salesDynamics')}
          subtitle={
            metric === 'revenue' && productFiltered
              ? t('dashboard.productFilterNote')
              : t('charts.salesDynamicsSub')
          }
          extra={<Segmented size='small' options={metricOptions} value={metric} onChange={setMetric} />}
        >
          <div className='wm-legend' style={{ marginBottom: 12 }}>
            <span>
              <i className='wm-swatch' style={{ background: palette.chart[0] }} />
              {t('charts.current')}
            </span>
            <span>
              <i className='wm-legend__dash' />
              {t('charts.previous')}
            </span>
          </div>
          {(metric === 'revenue' ? status.revenue.loading : status.reports.loading) ? (
            <ChartSkeleton height={300} />
          ) : (metric === 'revenue' ? status.revenue.error : status.reports.error) ? (
            <ErrorState
              onRetry={metric === 'revenue' ? status.revenue.refetch : status.reports.refetch}
            />
          ) : (
            <TrendChart
              data={trendSeries}
              valueFormatter={metricFormatter}
              currentLabel={t('charts.current')}
              previousLabel={t('charts.previous')}
            />
          )}
        </Card>

        <Card title={t('charts.productShare')} subtitle={t('charts.productShareSub')}>
          {status.reports.loading ? (
            <ChartSkeleton height={260} />
          ) : status.reports.error ? (
            <ErrorState compact onRetry={status.reports.refetch} />
          ) : shareData.length === 0 ? (
            <EmptyState compact title={t('states.emptyPeriod')} text={t('states.emptyHint')} />
          ) : (
            <DonutChart data={shareData} centerLabel={t('charts.totalUnits')} />
          )}
        </Card>
      </div>

      <div className='wm-grid wm-grid--2'>
        {isAdmin && (
          <Card
            title={t('charts.revenueVsCost')}
            subtitle={productFiltered ? t('dashboard.productFilterNote') : t('charts.revenueVsCostSub')}
            extra={
              <div className='wm-legend'>
                <span>
                  <i className='wm-swatch' style={{ background: palette.chart[0] }} />
                  {t('charts.cost')}
                </span>
                <span>
                  <i className='wm-swatch' style={{ background: palette.chart[2] }} />
                  {t('charts.profit')}
                </span>
              </div>
            }
          >
            {status.revenue.loading || status.reports.loading ? (
              <ChartSkeleton />
            ) : status.revenue.error || status.reports.error ? (
              <ErrorState onRetry={data.refetchAll} />
            ) : !hasAnySales ? (
              <EmptyState title={t('states.emptyPeriod')} text={t('states.emptyHint')} />
            ) : (
              <StackedBarChart
                data={data.revenueVsCost}
                valueFormatter={money}
                totalLabel={t('charts.revenue')}
                series={[
                  { key: 'cost', name: t('charts.cost'), color: palette.chart[0] },
                  { key: 'profit', name: t('charts.profit'), color: palette.chart[2] },
                ]}
              />
            )}
          </Card>
        )}

        <Card title={t('charts.topProducts')} subtitle={t('charts.topProductsSub')}>
          {status.reports.loading ? (
            <ChartSkeleton height={240} />
          ) : status.reports.error ? (
            <ErrorState onRetry={status.reports.refetch} />
          ) : data.topProducts.length === 0 ? (
            <EmptyState title={t('states.emptyPeriod')} text={t('states.emptyHint')} />
          ) : (
            <HBarChart
              data={data.topProducts}
              valueName={t('charts.units')}
              valueFormatter={v => `${formatNumber(v)} ${t('common.pcs')}`}
              color={palette.chart[1]}
            />
          )}
        </Card>

        <Card
          title={t('charts.byBranch')}
          subtitle={t('charts.byBranchSub')}
          extra={
            branchId !== 0 ? (
              <Button size='small' type='link' onClick={() => setBranchId(0)}>
                {t('filters.allBranches')}
              </Button>
            ) : undefined
          }
        >
          {branchId !== 0 || (!status.breakdown.loading && !data.breakdownEnabled) ? (
            <EmptyState icon={Store} title={t('charts.singleBranch')} compact />
          ) : status.breakdown.loading ? (
            <ChartSkeleton height={220} />
          ) : status.breakdown.error ? (
            <ErrorState onRetry={status.breakdown.refetch} />
          ) : data.branchRevenue.every(b => b.value === 0) ? (
            <EmptyState title={t('states.emptyPeriod')} text={t('states.emptyHint')} />
          ) : (
            <BranchRanking rows={data.branchRevenue} />
          )}
        </Card>

        {isAdmin && (
          <Card
            title={t('tables.lowStock')}
            subtitle={t('tables.lowStockSub', { count: LOW_STOCK_THRESHOLD })}
            extra={
              <Link to={ROUTE_URLS.PRODUCTS}>
                <Button size='small' type='link'>
                  {t('common.viewAll')}
                </Button>
              </Link>
            }
          >
            {status.products.loading ? (
              <LowStockSkeleton />
            ) : status.products.error ? (
              <ErrorState onRetry={status.products.refetch} />
            ) : kpis.lowStock.length === 0 ? (
              <EmptyState icon={Boxes} title={t('dashboard.allInStock')} compact />
            ) : (
              <ul className='wm-list'>
                {kpis.lowStock.slice(0, LOW_STOCK_PREVIEW).map(product => (
                  <li className='wm-list__item' key={product.id}>
                    <span className={`wm-notice__icon${product.quantity <= 0 ? ' wm-notice__icon--danger' : ''}`}>
                      {product.quantity <= 0 ? <PackageX size={15} /> : <TriangleAlert size={15} />}
                    </span>
                    <Link
                      to={createLink(ROUTE_URLS.EDIT_PRODUCT, product.id)}
                      className='wm-link-strong'
                      style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {product.name}
                    </Link>
                    <span className='wm-muted num nowrap'>
                      {formatNumber(product.quantity)} {t('common.pcs')}
                    </span>
                    <StockBadge quantity={product.quantity} />
                  </li>
                ))}
              </ul>
            )}
          </Card>
        )}
      </div>

      <DataTable
        title={
          <div>
            <h2 className='wm-card__title'>{t('tables.recentDays')}</h2>
            <div className='wm-card__subtitle'>
              {productFiltered ? t('dashboard.productFilterNote') : t('tables.recentDaysSub')}
            </div>
          </div>
        }
        toolbar={
          <Button
            icon={<Download size={15} />}
            onClick={exportDaily}
            disabled={data.dailyRows.length === 0}
          >
            {t('common.exportCsv')}
          </Button>
        }
        isLoading={status.revenue.loading || status.reports.loading}
        isError={status.revenue.error || status.reports.error}
        onRetry={data.refetchAll}
        columns={dailyColumns}
        dataSource={data.dailyRows}
        pageSize={10}
        sticky={false}
        emptyTitle={t('states.emptyPeriod')}
        emptyText={t('states.emptyHint')}
      />
    </>
  );
};

const BranchRanking = ({ rows }) => {
  const { t } = useI18n();
  const total = rows.reduce((acc, r) => acc + r.value, 0);
  const max = rows[0]?.value || 1;
  return (
    <div className='wm-rank'>
      {rows.slice(0, 8).map((row, i) => (
        <div className='wm-rank__item' key={row.id}>
          <span className='wm-rank__pos'>{i + 1}</span>
          <Link to={createLink(ROUTE_URLS.BRANCH_REPORTS, row.id)} className='wm-rank__name wm-link-strong'>
            {row.name}
          </Link>
          <span className='wm-rank__value'>
            {formatMoney(row.value)}
            <span className='wm-muted' style={{ fontWeight: 500, marginLeft: 8 }}>
              {formatPercent(total ? row.value / total : 0)}
            </span>
          </span>
          <span className='wm-rank__bar' aria-label={t('charts.share')}>
            <i style={{ width: `${(row.value / max) * 100}%`, animationDelay: `${i * 40}ms` }} />
          </span>
        </div>
      ))}
    </div>
  );
};

const LowStockSkeleton = () => (
  <div>
    {Array.from({ length: 4 }).map((_, i) => (
      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0' }}>
        <Skeleton width={30} height={30} radius={8} />
        <Skeleton width='45%' />
        <Skeleton width={60} style={{ marginLeft: 'auto' }} />
      </div>
    ))}
  </div>
);
